import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { History, Copy, ExternalLink, Calendar, Check, MessageSquare, Trash2, Search, Trash } from "lucide-react";
import { getBackend } from "../lib/backend";
import { ConfirmModal } from "./ConfirmModal";
import { Button } from "./ui/button";

interface HistoryItem {
    id: number;
    url: string;
    caption: string;
    comment: string;
    brand_name: string;
    created_at: string;
}

interface HistoryViewProps {
    onCopy: (text: string) => void;
}

const HistoryView: React.FC<HistoryViewProps> = ({ onCopy }) => {
    const [history, setHistory] = useState<HistoryItem[]>([]);
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState("");

    // Modal state
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
    const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);

    const fetchHistory = async () => {
        const backend = await getBackend();
        const data = await backend.getHistory();
        setHistory(data);
    };

    useEffect(() => {
        fetchHistory();
    }, []);

    const handleCopy = (text: string, id: number) => {
        onCopy(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const handleDelete = async (id: number) => {
        const backend = await getBackend();
        await backend.deleteHistory(id);
        fetchHistory();
        setConfirmDeleteId(null);
    };

    const handleDeleteAll = async () => {
        const backend = await getBackend();
        await backend.clearHistory();
        fetchHistory();
        setConfirmDeleteAll(false);
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const filteredHistory = history.filter(item =>
        item.comment.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.brand_name && item.brand_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.caption && item.caption.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-white">Generation History</h2>
                    <p className="text-white/60 text-sm">Review your past brand-aware comments</p>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                    <div className="relative w-full sm:w-64">
                        <input
                            type="text"
                            placeholder="Search history..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white placeholder:text-white/20 focus:outline-none focus:border-primary/50 pl-10 h-11"
                        />
                        <Search className="absolute left-3 top-3.5 text-white/20" size={16} />
                    </div>
                    {history.length > 0 && (
                        <Button
                            variant="destructive"
                            onClick={() => setConfirmDeleteAll(true)}
                            className="h-11 w-full sm:w-auto px-6 rounded-xl font-bold flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20"
                        >
                            <Trash size={16} />
                            <span>Delete All</span>
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
                {filteredHistory.length === 0 ? (
                    <div className="p-12 text-center bg-white/5 border border-white/10 rounded-3xl">
                        <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                            <History size={32} className="text-white/20" />
                        </div>
                        <p className="text-white/40">No history found matching your search.</p>
                    </div>
                ) : (
                    <AnimatePresence>
                        {filteredHistory.map((item, index) => (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="group relative p-5 bg-white/5 border border-white/10 rounded-2xl hover:border-white/20 transition-all overflow-hidden"
                            >
                                <div className="absolute top-0 left-0 w-1 h-full bg-primary/20 group-hover:bg-primary/50 transition-colors" />

                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex-1 space-y-2">
                                        <div className="flex items-center gap-3 text-[10px] text-white/40 uppercase tracking-widest font-bold">
                                            <span className="flex items-center gap-1">
                                                <Calendar size={12} />
                                                {formatDate(item.created_at)}
                                            </span>
                                            <span className="w-1 h-1 bg-white/20 rounded-full" />
                                            <span className="text-primary/60">{item.brand_name}</span>
                                        </div>

                                        <p className="text-white text-sm font-medium pr-12 line-clamp-2 leading-relaxed">
                                            "{item.comment}"
                                        </p>

                                        <div className="flex items-center gap-4 pt-1">
                                            <a
                                                href={item.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white transition-colors"
                                            >
                                                <ExternalLink size={12} />
                                                <span>View Post</span>
                                            </a>
                                            <div className="flex items-center gap-1.5 text-xs text-white/20">
                                                <MessageSquare size={12} />
                                                <span className="line-clamp-1 max-w-[200px]">{item.caption || "No caption provided"}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleCopy(item.comment, item.id)}
                                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all shrink-0 ${copiedId === item.id
                                                ? "bg-green-500/20 text-green-400 border border-green-500/30"
                                                : "bg-white/5 hover:bg-white/10 text-white border border-white/10"
                                                }`}
                                        >
                                            {copiedId === item.id ? (
                                                <>
                                                    <Check size={16} />
                                                    <span>Copied</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Copy size={16} />
                                                    <span>Copy</span>
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => setConfirmDeleteId(item.id)}
                                            className="p-2.5 hover:bg-red-500/10 text-white/20 hover:text-red-400 rounded-xl transition-colors"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                )}
            </div>

            {/* Confirmation Modals */}
            <ConfirmModal
                isOpen={confirmDeleteId !== null}
                onClose={() => setConfirmDeleteId(null)}
                onConfirm={() => confirmDeleteId && handleDelete(confirmDeleteId)}
                title="Delete History Item"
                message="Are you sure you want to delete this comment from your history? This action cannot be undone."
                confirmText="Delete"
                variant="destructive"
            />

            <ConfirmModal
                isOpen={confirmDeleteAll}
                onClose={() => setConfirmDeleteAll(false)}
                onConfirm={handleDeleteAll}
                title="Clear All History"
                message="Are you sure you want to clear your entire generation history? This will permanently delete all saved comments."
                confirmText="Clear All"
                variant="destructive"
            />
        </div>
    );
};

export default HistoryView;
